import { useFadeIn } from '@/hooks/useFadeIn';

interface MechStepProps {
  icon: string;
  title: string;
  desc: string;
  delay: number;
}

function MechStep({ icon, title, desc, delay }: MechStepProps) {
  const fade = useFadeIn({ direction: 'up', delay });
  return (
    <div
      ref={fade.ref}
      style={fade.style}
      className="bg-white rounded-lg border-t border-gray-200 p-6 md:p-8 w-full flex-1"
    >
      <div className="mb-4 flex justify-center">
        <div className="w-12 h-12 flex items-center justify-center">
          <i className={`${icon} text-2xl`} style={{ color: '#3E5F8F' }}></i>
        </div>
      </div>
      <h3 className="font-serif text-gray-900 text-base md:text-lg text-center leading-relaxed mb-3">
        {title}
      </h3>
      <p className="font-light text-gray-500 text-sm text-center leading-loose">{desc}</p>
    </div>
  );
}

const steps = [
  {
    icon: 'ri-tooth-line',
    title: '治療前',
    desc: '溶け出した部分に空気が入り込み、光が乱反射して白く見える状態です。',
  },
  {
    icon: 'ri-drop-line',
    title: 'ICONの作用',
    desc: '専用ジェルで歯の表面を開き、透明な樹脂を内部へ浸透させます。',
  },
  {
    icon: 'ri-sun-line',
    title: '治療後',
    desc: '樹脂が空気を置き換えて光の屈折が整い、自然な透明感になります。',
  },
];

export default function IconMechanism() {
  const heading = useFadeIn({ direction: 'up' });

  return (
    <section className="bg-white py-14 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div ref={heading.ref} style={heading.style} className="text-center">
          <span
            className="inline-block font-medium text-xs md:text-sm tracking-[0.2em]"
            style={{ color: '#3E5F8F' }}
          >
            How It Works
          </span>
          <div className="w-8 h-px mt-3 mb-4 mx-auto" style={{ backgroundColor: '#3E5F8F' }}></div>
          <h2 className="font-serif text-gray-900 text-2xl md:text-3xl leading-relaxed mb-4">
            どうして白い点が消えるのか
          </h2>
          <p className="font-light text-gray-500 text-sm md:text-base leading-loose max-w-2xl mx-auto">
            歯の内部の「空気」が光を乱反射させることで白く見えています。<br />
            ICONはその空気を「透明な樹脂」に置き換えることで、<br />
            周囲の歯と同じ色調に整えます。
          </p>
        </div>

        {/* デスクトップ: 横並び */}
        <div className="hidden md:flex items-stretch justify-center gap-0 mt-10 md:mt-12">
          {steps.map((step, i) => (
            <div key={step.title} className="flex items-center">
              <div className="w-56 lg:w-64">
                <MechStep icon={step.icon} title={step.title} desc={step.desc} delay={i * 100} />
              </div>
              {i < steps.length - 1 && (
                <div className="flex items-center justify-center w-12 flex-shrink-0">
                  <i className="ri-arrow-right-line text-xl text-gray-300"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* モバイル: 縦並び */}
        <div className="flex md:hidden flex-col items-center gap-0 mt-10">
          {steps.map((step, i) => (
            <div key={step.title} className="w-full">
              <MechStep icon={step.icon} title={step.title} desc={step.desc} delay={i * 100} />
              {i < steps.length - 1 && (
                <div className="flex items-center justify-center py-3">
                  <i className="ri-arrow-down-line text-xl text-gray-300"></i>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}